import React, {useEffect, useState} from 'react';
import {Box, CircularProgress, Collapse, Stack, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {TAGS_SECTION_HEADING_SX} from '../../collection-requests/tags/EntityTagsSection';
import {enrichLinkedEeis} from '../enrichLinkedEeis';
import LinkedEeiCard from './LinkedEeiCard';

/**
 * Search-page-style linked EEI cards with async enrichment.
 */
export default function LinkedEeiCardsSection({
    linkedEeis = [],
    showHeading = false,
    heading = 'EEI',
    collapsible = false,
    defaultExpanded = true,
    sx,
}) {
    const [eeis, setEeis] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(defaultExpanded);

    useEffect(() => {
        if (!linkedEeis.length) {
            setEeis([]);
            setLoading(false);
            setError(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        enrichLinkedEeis(linkedEeis)
            .then((next) => {
                if (!cancelled) {
                    setEeis(next);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(e?.message || 'Failed to load EEI details');
                    setEeis(linkedEeis.map((linked) => ({
                        id: linked.id,
                        number: linked.eeiNumber || linked.id,
                        eeiNumber: linked.eeiNumber,
                        question: linked.text,
                        text: linked.text,
                        type: 'EEI',
                    })));
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [linkedEeis]);

    if (!linkedEeis.length && !loading) {
        return null;
    }

    const cards = (
        <Stack data-testid="iir-eeis-row" spacing={1}>
            {loading && (
                <Stack alignItems="center" py={2}>
                    <CircularProgress size={24}/>
                </Stack>
            )}
            {error && (
                <Typography color="error" variant="body2">{error}</Typography>
            )}
            {!loading && !error && eeis.map((eei) => (
                <LinkedEeiCard key={eei.id || eei.number || eei.eeiNumber} item={eei}/>
            ))}
        </Stack>
    );

    return (
        <Stack spacing={1} sx={sx}>
            {showHeading && (
                <Box
                    component={collapsible ? 'button' : 'div'}
                    type={collapsible ? 'button' : undefined}
                    onClick={collapsible ? () => setExpanded((open) => !open) : undefined}
                    aria-expanded={collapsible ? expanded : undefined}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        border: 'none',
                        bgcolor: 'transparent',
                        p: 0,
                        m: 0,
                        cursor: collapsible ? 'pointer' : 'default',
                        textAlign: 'left',
                    }}
                >
                    <Typography sx={TAGS_SECTION_HEADING_SX}>{heading}</Typography>
                    {collapsible && (
                        <ExpandMoreIcon
                            aria-hidden
                            sx={{
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 0.2s ease',
                            }}
                        />
                    )}
                </Box>
            )}
            {collapsible ? (
                <Collapse in={expanded}>{cards}</Collapse>
            ) : cards}
        </Stack>
    );
}
